import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import TranslateButton from "./TranslateButton";

/**
 * ChatPanel — collapsible in-order chat between customer, driver/supplier (and admin).
 * - Polls GET /api/orders/:id/messages every 3s while open.
 * - Uses `since` cursor to only fetch new messages after the first load.
 */
export default function ChatPanel({ orderId, peerName, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sinceRef = useRef(null);
  const timer = useRef(null);

  const fetchMessages = async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/orders/${orderId}/messages`, { params });
      if (data.length > 0) {
        setMessages((prev) => {
          const merged = [...prev, ...data];
          // dedupe by message_id
          const seen = new Set();
          return merged.filter((m) => (seen.has(m.message_id) ? false : (seen.add(m.message_id), true)));
        });
        sinceRef.current = data[data.length - 1].created_at;
        if (!open) {
          // Count as unread only if they are not from me
          const fromOthers = data.filter((m) => m.sender_id !== user?.id).length;
          if (fromOthers > 0) setUnread((u) => u + fromOthers);
        }
      }
    } catch (_) {}
  };

  useEffect(() => {
    // Initial load once
    fetchMessages();
    // Poll while mounted (chat runs in background so unread badge stays live)
    timer.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      // scroll to bottom
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    }
  }, [open, messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { text: t });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      setText("");
    } catch (_) {}
    finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className={`mt-3 ${className}`} data-testid={`chat-panel-${orderId}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-ink-surface2 border border-ink-border rounded-2xl px-3 py-2 text-xs font-black text-white hover:border-ink-borderStrong"
        data-testid={`chat-toggle-${orderId}`}
      >
        <span className="flex items-center gap-2">
          <MessageCircle size={14} className="text-brand-accent" />
          {open ? "Chat" : `Chat${peerName ? " with " + peerName : ""}`}
          {!open && unread > 0 && (
            <span className="bg-brand-warn text-white rounded-full px-1.5 py-0.5 text-[10px] font-black" data-testid={`chat-unread-${orderId}`}>
              {unread}
            </span>
          )}
        </span>
        {open ? <X size={14} /> : <span className="text-[10px] text-ink-textDim uppercase">Open</span>}
      </button>

      {open && (
        <div className="mt-2 bg-ink-surface border border-ink-border rounded-2xl overflow-hidden">
          <div ref={scrollRef} className="max-h-56 overflow-y-auto px-3 py-2 space-y-2" data-testid={`chat-messages-${orderId}`}>
            {messages.length === 0 && (
              <div className="text-[11px] text-ink-textDim text-center py-4">No messages yet — say hi 👋</div>
            )}
            {messages.map((m) => {
              const isSystem = m.sender_role === "system";
              if (isSystem) {
                return (
                  <div key={m.message_id} className="flex justify-center" data-testid={`chat-msg-${m.message_id}`}>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-electric-teal/80 bg-electric-teal/8 border border-electric-teal/25 rounded-full px-3 py-1 font-bold">
                      · {m.text} ·
                    </div>
                  </div>
                );
              }
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.message_id}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-[12px] ${mine ? "bg-white text-black" : "bg-ink-surface2 text-white border border-ink-border"}`}>
                    <div className="text-[9px] uppercase font-black opacity-60 mb-0.5">
                      {m.sender_name} · {m.sender_role}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    {!mine && <TranslateButton messageId={m.message_id} text={m.text} />}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 border-t border-ink-border p-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type a message…"
              className="flex-1 bg-ink-surface2 border border-ink-border rounded-full px-3 py-2 text-xs text-white outline-none focus:border-white"
              data-testid={`chat-input-${orderId}`}
            />
            <button
              onClick={send}
              disabled={sending || !text.trim()}
              className="bg-brand-accent text-white rounded-full w-9 h-9 flex items-center justify-center disabled:opacity-50"
              data-testid={`chat-send-${orderId}`}
            >
              {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
